import { useEditable } from '@/components/Editable';
import { FunctionHeader } from '@/components/FunctionHeader';
import { ListViewBody, ListViewLabel } from '@/components/ListView';
import { useAiSuggestStatusChecker } from '@/features/invoices/components/Details/Components/Context/AiSuggestStatus';
import { useOperationStatusChecker } from '@/features/invoices/components/Details/Components/Context/OperationStatus';
import { useIsView } from '@/features/invoices/components/Details/Components/Context/ViewEdit';
import { useDisplayedOperationLabel } from '@/features/invoices/components/Details/Components/ReceivedInvoice/hooks/useDisplayedOperationLabel';
import { useReceivedInvoiceLabel } from '@/features/invoices/components/Details/Components/ReceivedInvoice/hooks/useReceivedInvoice';
import { ReceivedInvoiceData } from '@/features/invoices/components/Details/Components/ReceivedInvoice/type';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useTranslation } from '@/i18n';
import {
  Bottom,
  ButtonV2,
  ListView,
  Top,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import { LabelTagAI } from '../../LabelTagAI';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

export type Props = {
  data: ReceivedInvoiceData;
};

export const View: FC<Props> = memo(({ data }) => {
  const flag = useGetFlags('RECEIVED_INVOICE_EDIT_PAYEE');
  const flagEDocMetadata = useGetFlags('E_DOC_METADATA');
  const { onEdit } = useIsView();
  const _isEditable = useEditable('ReceivedInvoiceEdit');
  const { t } = useTranslation();
  const {
    operation: operationLabel,
    updateAt: updateAtLabel,
    updateType: updateTypeLabel,
    payee: payeeLabel,
    pics: picsLabel,
    receiptType: receiptTypeLabel,
    memo: memoLabel,
    bookDate: bookDateLabel,
    uploader: uploaderLabel,
  } = useReceivedInvoiceLabel(data);
  const operationChecker = useOperationStatusChecker();
  const aiSuggestChecker = useAiSuggestStatusChecker();
  const isEditable = useMemo(() => {
    if (!_isEditable) {
      return _isEditable;
    }
    if (
      operationChecker.isEdit ||
      operationChecker.isApplied ||
      operationChecker.isArchived ||
      operationChecker.isOcrProcessing ||
      aiSuggestChecker.isProcessing
    ) {
      return false;
    }
    return true;
  }, [_isEditable, operationChecker, aiSuggestChecker.isProcessing]);
  const displayedOperationLabel: string =
    useDisplayedOperationLabel(operationLabel);
  const leftRightRatio = useMemo(
    () => ({
      left: {
        value: '30%',
      },
      right: {
        value: '70%',
      },
    }),
    []
  );
  return (
    <FunctionHeader
      functionName={t('received_invoice_info')}
      button={
        isEditable && (
          <ButtonV2
            color='primary'
            size='xs'
            isSecondary
            onClick={onEdit}
            testId='edit-received-invoice'
          >
            {t('edit')}
          </ButtonV2>
        )
      }
    >
      <div className={cx(styles['container'])}>
        <ListView
          leftRightRatio={leftRightRatio}
          testId='received-invoice-view'
        >
          {flag && (
            <Top>
              <ListViewLabel key='left'>
                {t('received_invoice_payee')}
              </ListViewLabel>
              <ListViewBody key='right'>{payeeLabel}</ListViewBody>
            </Top>
          )}
          <Top>
            <ListViewLabel key='left'>
              {t('received_invoice_pics')}
            </ListViewLabel>
            <ListViewBody key='right'>
              <div className={cx(styles['pics'])}>{picsLabel}</div>
            </ListViewBody>
          </Top>
          <Top>
            <ListViewLabel key='left'>
              {t('received_invoice_operation_status')}
            </ListViewLabel>
            <ListViewBody key='right'>{displayedOperationLabel}</ListViewBody>
          </Top>
          <Top>
            <ListViewLabel key='left'>
              {t('received_invoice_update_at')}
            </ListViewLabel>
            <ListViewBody key='right'>
              {updateAtLabel}
              {uploaderLabel}
            </ListViewBody>
          </Top>
          <Top>
            <ListViewLabel key='left'>
              {t('received_invoice_update_type')}
            </ListViewLabel>
            <ListViewBody key='right'>
              <div className={cx(['updateTypeContainer'])}>
                {/* need check isAI to show label */}
                {updateTypeLabel} {data.isCreatedByAI && <LabelTagAI />}
              </div>
            </ListViewBody>
          </Top>
          <Top>
            <ListViewLabel key='left'>
              {t('received_invoice_receipt_type')}
            </ListViewLabel>
            <ListViewBody key='right'>{receiptTypeLabel}</ListViewBody>
          </Top>
          {!flag && (
            <Top>
              <ListViewLabel key='left'>
                {t('received_invoice_payee')}
              </ListViewLabel>
              <ListViewBody key='right'>{payeeLabel}</ListViewBody>
            </Top>
          )}
          {flagEDocMetadata && (
            <Top>
              <ListViewLabel key='left'>
                {t('received_invoice_book_date')}
              </ListViewLabel>
              <ListViewBody key='right'>{bookDateLabel}</ListViewBody>
            </Top>
          )}
          <Bottom>
            <ListViewLabel key='left'>{t('memo')}</ListViewLabel>
            <ListViewBody
              key='right'
              className={cx({ [styles['memoContainer']]: !!memoLabel })}
            >
              {memoLabel}
            </ListViewBody>
          </Bottom>
        </ListView>
      </div>
    </FunctionHeader>
  );
});
View.displayName = 'ReceivedInvoiceView';
